import React, { useLayoutEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { tv } from 'tailwind-variants'

import type { Option, OptionValue } from '@ui-kit/components/formComponents/Select/types'
import { getOptionIdentifier } from '@ui-kit/components/formComponents/Select/utils'
import { body2 } from '@ui-kit/core/typography'

const MAX_HEIGHT = 210
const VIEWPORT_MARGIN = 8

const optionsContainer = tv({
  base: 'fixed z-[9999] overflow-auto border border-solid border-grey-300 bg-white shadow-lg',
  variants: {
    placement: {
      below: 'rounded-b-lg border-t-0',
      above: 'rounded-t-lg border-b-0',
    },
  },
})

interface Props {
  options: Option[]
  name: string
  setActiveMenuOption: (activeOption: string) => void
  activeMenuOption: string
  onSelectOptionHandler: (optionValue: OptionValue) => void
  anchorRef: React.RefObject<HTMLElement | null>
}

type Placement = 'above' | 'below'

type Position = {
  left: number
  width: number
  maxHeight: number
  placement: Placement
  offset: number
}

const OptionsContainerComponent = (props: Props) => {
  const [position, setPosition] = useState<Position | null>(null)

  useLayoutEffect(
    function syncDropdownPositionWithAnchor() {
      const anchor = props.anchorRef.current
      if (!anchor) {
        return
      }

      const update = () => {
        const rect = anchor.getBoundingClientRect()
        const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_MARGIN
        const spaceAbove = rect.top - VIEWPORT_MARGIN
        const shouldPlaceAbove = spaceBelow < MAX_HEIGHT && spaceAbove > spaceBelow
        const availableSpace = shouldPlaceAbove ? spaceAbove : spaceBelow

        setPosition({
          left: rect.left,
          width: rect.width,
          placement: shouldPlaceAbove ? 'above' : 'below',
          offset: shouldPlaceAbove ? window.innerHeight - rect.top : rect.bottom,
          maxHeight: Math.max(Math.min(MAX_HEIGHT, availableSpace), 0),
        })
      }

      update()

      window.addEventListener('scroll', update, true)
      window.addEventListener('resize', update)

      return () => {
        window.removeEventListener('scroll', update, true)
        window.removeEventListener('resize', update)
      }
    },
    [props.anchorRef]
  )

  if (!position) {
    return null
  }

  const verticalStyle =
    position.placement === 'above' ? { bottom: position.offset } : { top: position.offset }

  return createPortal(
    <ul
      data-select-dropdown={props.name}
      className={optionsContainer({ placement: position.placement })}
      style={{
        left: position.left,
        width: position.width,
        maxHeight: position.maxHeight,
        ...verticalStyle,
      }}
    >
      {props.options.map((option) => (
        <li
          className="px-4 py-2.5 aria-selected:bg-grey-200"
          key={option.label}
          id={getOptionIdentifier(option.value, props.name)}
          onMouseEnter={() =>
            props.setActiveMenuOption(getOptionIdentifier(option.value, props.name))
          }
          onClick={() => props.onSelectOptionHandler(option.value)}
          aria-selected={props.activeMenuOption === getOptionIdentifier(option.value, props.name)}
          role="option"
          tabIndex={0}
        >
          <span className={body2()} id={`drop-down-option-${option.value}`}>
            {option.label}
          </span>
        </li>
      ))}
    </ul>,
    document.body
  )
}

export default OptionsContainerComponent
